import React, { useState } from 'react';
import { Fade, Grid, IconButton, Modal, Typography } from '@mui/material';
import { Box } from '@mui/system';
import Button from 'components/atoms/Button';
import { EButtonSizes, EButtonVariants } from 'constants/Buttons';
import { useTranslations } from 'hooks/useTranslations';
import { colors } from 'theme/colors';
import Icon from 'components/atoms/Icon/Icon';
import { EIconTypes } from 'constants/Icons';
import { formatDate } from 'utils/date';
import { StyledRouterLink } from 'components/atoms/RouterLink/styles';
import { ERouteLinks } from 'models/route';
import { Chips } from 'components/atoms/Chips';
import { useDictionary } from 'models/dictionaryContext';
import { formatAddress } from 'utils/legalAddress';
import { renderDataColorByValues } from 'components/molecules/Table/helpers';
import { useThemeBreakpoint } from 'hooks/useThemeBreakpoint';
import { useGlobalProperty } from 'models/globalPropertyContext';
import {
  getDictionaryValue,
  getLicenceStatusChipVariant,
  getPaymentStatusChipVariant,
} from 'pages/LegalEntitiesPage/LegalEntityViewLicencePage/helpers';
import { EArrayType, EComparisonCategory, generateComparisonSections } from './helpers';
import {
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ComparisonTable,
  FirstColumn,
  ValueColumn,
  EmptyHeaderCell,
  BeforeHeaderCell,
  AfterHeaderCell,
  StateChipsContainer,
  ValuesListContainer,
  LinkedValueWrapper,
  DataRowFirstCell,
  DataRowValueCell,
  CategoryHeaderFirstCell,
  CategoryHeaderNoChangesCell,
  CategoryHeaderEmptyCell,
} from './styles';
import { MobileComparisonView } from './MobileComparisonView';
import { MobileComparisonHeader, MobileComparisonRow } from './MobileComparisonView/styles';

interface ILicenceHistoryDetailsModalProps {
  open: boolean;
  historyDetailsData: any;
  onClose: () => void;
}

export const LicenceHistoryDetailsModal = ({
  open,
  historyDetailsData,
  onClose,
}: ILicenceHistoryDetailsModalProps) => {
  const { t } = useTranslations();
  const { isDesktop } = useThemeBreakpoint();
  const { longDateFormat } = useGlobalProperty();
  const [weaponDetailOpen, setWeaponDetailOpen] = useState<string>('');
  const [attachmentDetailOpen, setAttachmentDetailOpen] = useState<string>('');
  const {
    weaponLicenceStatus,
    gender,
    nationality,
    civilianPrefix,
    identificationDocumentType,
    country,
    region,
    weaponLicenceCategory,
    weaponLicenceCompetency,
    weaponLicenceCondition,
    weaponLicenceGoodReason,
    state,
    attachmentDocumentTypeData,
    weaponLicencePaymentStatus,
    organisationStateType,
    organisationCivilianType,
    organisationMilitaryType,
    organisationPoliceType,
  } = useDictionary();

  const handleClose = () => {
    setAttachmentDetailOpen('');
    setWeaponDetailOpen('');
    onClose();
  };

  const comparisonSections = historyDetailsData
    ? generateComparisonSections(
        historyDetailsData.previous,
        historyDetailsData.post,
        historyDetailsData.historyStatus,
      )
    : [];

  const dictionaries = {
    gender: gender || [],
    nationality: nationality || [],
    civilianPrefix: civilianPrefix || [],
    identificationDocumentType: identificationDocumentType || [],
    country: country || [],
    region: region || [],
    weaponLicenceCategory: weaponLicenceCategory || [],
    weaponLicenceCompetency: weaponLicenceCompetency || [],
    weaponLicenceCondition: weaponLicenceCondition || [],
    weaponLicenceGoodReason: weaponLicenceGoodReason || [],
    weaponLicencePaymentStatus: weaponLicencePaymentStatus || [],
    state: state || [],
    documentType: attachmentDocumentTypeData || [],
    organisationType: [
      ...(organisationStateType ?? []),
      ...(organisationCivilianType ?? []),
      ...(organisationMilitaryType ?? []),
      ...(organisationPoliceType ?? []),
    ],
  };

  const formatValue = (value: any, row: any): string | React.ReactNode => {
    if (value === null || value === undefined || value === '') {
      return '';
    }

    if (row.isTranslatable && typeof value === 'string') {
      return t(value);
    }

    if (row.isFirearmLink && row.firearmIdKey && row.weaponContext) {
      const weapon = row.weaponContext.after || row.weaponContext.before;
      const firearmId = weapon?.[row.firearmIdKey];
      if (firearmId && value) {
        return (
          <StyledRouterLink to={ERouteLinks.ViewRegisteredFirearm.replace(':id', firearmId)}>
            {value}
          </StyledRouterLink>
        );
      }
    }

    if (row.isLegalEntityLink && row.legalEntityIdKey && row.weaponContext) {
      const weapon = row.weaponContext.after || row.weaponContext.before;
      const entityId = weapon?.[row.legalEntityIdKey];
      if (entityId && value) {
        return (
          <StyledRouterLink to={ERouteLinks.ViewLegalEntity.replace(':id', entityId)}>
            {value}
          </StyledRouterLink>
        );
      }
    }
    if (row.isDictionary && row.dictionaryType === 'state') {
      const dict = dictionaries[row.dictionaryType as keyof typeof dictionaries] || [];
      const statusText = getDictionaryValue(value, dict);
      return statusText;
    }

    if (row.isDictionary && row.dictionaryType === 'weaponLicencePaymentStatus') {
      const dict = dictionaries[row.dictionaryType as keyof typeof dictionaries] || [];
      const statusText = getDictionaryValue(value, dict);
      const chipVariant = getPaymentStatusChipVariant(value);
      return <Chips text={statusText} fullWidth variant={chipVariant} />;
    }

    if (row.isWeaponList && Array.isArray(value)) {
      if (value.length === 0) return '';

      const propertyKey = row.weaponPropertyKey;
      const values = value.map((weapon) => {
        if (propertyKey.includes('.')) {
          const keys = propertyKey.split('.');
          let val = weapon;
          // eslint-disable-next-line no-restricted-syntax
          for (const key of keys) {
            val = val?.[key];
          }
          return val;
        }
        return weapon[propertyKey];
      });

      if (row.isDictionary && row.dictionaryType === 'state') {
        const dict = dictionaries[row.dictionaryType as keyof typeof dictionaries] || [];
        return (
          <StateChipsContainer>
            {values.map((val) => {
              const stateText = getDictionaryValue(val, dict);
              const chipVariant = renderDataColorByValues('', '', val).stateField;
              return (
                <div key={`${val}-${stateText}`}>
                  {val ? (
                    <Chips text={stateText} fullWidth variant={chipVariant as any} />
                  ) : null}
                </div>
              );
            })}
          </StateChipsContainer>
        );
      }

      if (row.isDictionary && row.dictionaryType) {
        const dict = dictionaries[row.dictionaryType as keyof typeof dictionaries] || [];
        return (
          <ValuesListContainer>
            {values.map((val) => (
              <div key={`${val}-${getDictionaryValue(val, dict)}`}>
                {getDictionaryValue(val, dict) || val || ''}
              </div>
            ))}
          </ValuesListContainer>
        );
      }

      return (
        <ValuesListContainer>
          {/* eslint-disable react/no-array-index-key */}
          {values.map((val, idx) => {
            if (row.isFirearmLink && row.firearmIdKey) {
              const weapon = value[idx];
              const firearmId = weapon?.[row.firearmIdKey];
              if (firearmId && val) {
                return (
                  <LinkedValueWrapper key={`firearm-${firearmId}-${val}`}>
                    <StyledRouterLink
                      to={ERouteLinks.ViewRegisteredFirearm.replace(':id', firearmId)}
                    >
                      {val}
                    </StyledRouterLink>
                  </LinkedValueWrapper>
                );
              }
            }
            if (row.isLegalEntityLink && row.legalEntityIdKey) {
              const weapon = value[idx];
              const entityId = weapon?.[row.legalEntityIdKey];
              if (entityId && val) {
                return (
                  <LinkedValueWrapper key={`entity-${entityId}-${val}`}>
                    <StyledRouterLink
                      to={ERouteLinks.ViewLegalEntity.replace(':id', entityId)}
                    >
                      {val}
                    </StyledRouterLink>
                  </LinkedValueWrapper>
                );
              }
            }
            return <div key={`value-${idx}-${val}`}>{val || ''}</div>;
          })}
          {/* eslint-enable react/no-array-index-key */}
        </ValuesListContainer>
      );
    }

    if (row.isAddress) {
      if (!value) return '';
      return formatAddress(value, country || []);
    }

    if (row.isHtml) {
      try {
        const parsed = JSON.parse(value);
        if (Array.isArray(parsed)) {
          return parsed
            .map((node: any) => node.children?.map((child: any) => child.text).join('') || '')
            .join(' ');
        }
      } catch (e) {
        return value;
      }
    }

    if (row.isDate) {
      return formatDate(value, 'dd/MM/yyyy');
    }

    if (row.isArray && Array.isArray(value)) {
      if (row.arrayType === EArrayType.Dictionary && row.dictionaryType) {
        const dict = dictionaries[row.dictionaryType as keyof typeof dictionaries] || [];
        return value.map((id) => getDictionaryValue(id, dict)).join(', ') || '';
      }
      return `${value.length} items`;
    }

    if (row.isDictionary && row.dictionaryType) {
      const dict = dictionaries[row.dictionaryType as keyof typeof dictionaries] || [];
      return getDictionaryValue(value, dict) || value;
    }

    if (typeof value === 'object') {
      return JSON.stringify(value);
    }

    return value;
  };

  return (
    <Modal
      aria-labelledby="licence-history-details-modal-title"
      aria-describedby="licence-history-details-modal-description"
      open={open}
      onClose={handleClose}
      closeAfterTransition
      sx={{
        display: 'flex',
        alignItems: { xs: 'flex-start', sm: 'center' },
        justifyContent: 'center',
        padding: { xs: '0', sm: '20px' },
        '& .MuiBackdrop-root': {
          backgroundColor: colors.modalBackdrop,
        },
      }}
    >
      <Fade in={open}>
        <ModalContent>
          <ModalHeader>
            <Typography
              variant="h5"
              sx={{
                color: colors.primary500,
                margin: 0,
              }}
            >
              {t('legalEntity.licenceDetails.history.details.title')}
            </Typography>
            <IconButton
              onClick={handleClose}
              sx={{
                padding: 0,
                width: '24px',
                height: '24px',
              }}
            >
              <Icon type={EIconTypes.close} />
            </IconButton>
          </ModalHeader>

          <ModalBody>
            {historyDetailsData && (
              <>
                <Grid
                  container
                  sx={{
                    backgroundColor: colors.gray100,
                    border: `1px solid ${colors.gray200}`,
                    minHeight: isDesktop ? '84px' : 'auto',
                    borderRadius: '4px',
                    marginBottom: '24px',
                    padding: isDesktop ? '' : '12px',
                  }}
                >
                  <Grid
                    size={{ xs: 12, md: 3 }}
                    sx={{
                      padding: isDesktop ? '16px' : '8px',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{ marginBottom: '4px', fontSize: '12px', fontWeight: 600 }}
                    >
                      {t('legalEntity.licenceDetails.history.dateTime.column')}
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: '14px' }}>
                      {formatDate(historyDetailsData.createdDate, longDateFormat)}
                    </Typography>
                  </Grid>

                  <Grid
                    size={{ xs: 12, md: 3 }}
                    sx={{
                      padding: isDesktop ? '16px' : '8px',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{ marginBottom: '4px', fontSize: '12px', fontWeight: 600 }}
                    >
                      {t('legalEntity.licenceDetails.history.user.column')}
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: '14px' }}>
                      {historyDetailsData.createdBy ? (
                        <StyledRouterLink
                          to={ERouteLinks.ViewUser.replace(
                            ':id',
                            historyDetailsData.createdBy,
                          )}
                        >
                          {historyDetailsData.createdByName}
                        </StyledRouterLink>
                      ) : (
                        historyDetailsData.createdByName
                      )}
                    </Typography>
                  </Grid>

                  <Grid
                    size={{ xs: 12, md: 3 }}
                    sx={{
                      padding: isDesktop ? '16px' : '8px',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{ marginBottom: '4px', fontSize: '12px', fontWeight: 600 }}
                    >
                      {t('legalEntity.licenceDetails.history.description.column')}
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: '14px' }}>
                      {historyDetailsData.description ? t(historyDetailsData.description) : ''}
                    </Typography>
                  </Grid>

                  <Grid
                    size={{ xs: 12, md: 3 }}
                    sx={{
                      padding: isDesktop ? '16px' : '8px',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{ marginBottom: '4px', fontSize: '12px', fontWeight: 600 }}
                    >
                      {t('legalEntity.licenceDetails.history.licenceStatus.column')}
                    </Typography>
                    <Typography variant="body2">
                      <Chips
                        text={getDictionaryValue(
                          historyDetailsData.licenceStatusUniqueId,
                          weaponLicenceStatus || [],
                        )}
                        variant={getLicenceStatusChipVariant(
                          historyDetailsData.licenceStatusUniqueId,
                        )}
                        fullWidth
                      />
                    </Typography>
                  </Grid>
                </Grid>

                {isDesktop ? (
                  <Box
                    sx={{
                      marginTop: '20px',
                      borderRadius: '4px',
                      overflow: 'hidden',
                    }}
                  >
                    <ComparisonTable>
                      <colgroup>
                        <FirstColumn />
                        <ValueColumn />
                        <ValueColumn />
                      </colgroup>
                      <thead>
                        <tr>
                          <EmptyHeaderCell>
                            {/* Empty header for field names column */}
                          </EmptyHeaderCell>
                          <BeforeHeaderCell>
                            {t('legalEntity.licenceDetails.history.before.column')}
                          </BeforeHeaderCell>
                          <AfterHeaderCell>
                            {t('legalEntity.licenceDetails.history.after.column')}
                          </AfterHeaderCell>
                        </tr>
                      </thead>
                      <tbody>
                        {(() => {
                          const lastVisibleSection =
                            comparisonSections[comparisonSections.length - 1];

                          const weaponSections = comparisonSections.filter((section) =>
                            section.categoryName.includes(EComparisonCategory.Weapon),
                          );
                          const attachmentSections = comparisonSections.filter((section) =>
                            section.categoryName.includes(EComparisonCategory.Attachments),
                          );
                          const hasMultiple =
                            weaponSections.length > 1 || attachmentSections.length > 1;

                          const additionalInfo =
                            weaponSections[weaponSections.length - 1]?.additionalRow ?? null;

                          return comparisonSections.map((section, sectionIndex) => {
                            const hasChanges = section.rows.length > 0;

                            const isFirstSection = sectionIndex === 0;
                            const isLastVisibleSection = section === lastVisibleSection;

                            let sectionTitle = t(section.categoryTranslationKey);

                            let showCategoryHeaderFirstCell = true;
                            let showSortIcon = false;

                            if (section.categoryName.includes(EComparisonCategory.Weapon)) {
                              showSortIcon = true;
                              if (
                                section.weaponIndex !== undefined &&
                                section.weaponIndex > 0
                              ) {
                                showCategoryHeaderFirstCell = false;
                              }

                              if (section.weaponCategory) {
                                const categoryText = getDictionaryValue(
                                  section.weaponCategory,
                                  weaponLicenceCategory || [],
                                );
                                if (categoryText) {
                                  sectionTitle = `${sectionTitle} (${categoryText})`;
                                }
                              }
                            }
                            if (
                              section.categoryName.includes(EComparisonCategory.Attachments)
                            ) {
                              showSortIcon = true;
                              if (
                                section.weaponIndex !== undefined &&
                                section.weaponIndex > 0
                              ) {
                                showCategoryHeaderFirstCell = false;
                              }
                            }

                            return (
                              <React.Fragment key={section.categoryName}>
                                {!!showCategoryHeaderFirstCell && (
                                  <tr>
                                    <CategoryHeaderFirstCell
                                      style={{
                                        borderTop: isFirstSection
                                          ? `1px solid ${colors.primary50}`
                                          : 'none',
                                        borderBottom: `1px solid ${colors.primary50}`,
                                        ...(isFirstSection && { borderTopLeftRadius: '4px' }),
                                      }}
                                    >
                                      {sectionTitle}
                                    </CategoryHeaderFirstCell>
                                    {!hasChanges && (
                                      <>
                                        <CategoryHeaderNoChangesCell
                                          style={{
                                            borderTop: isFirstSection
                                              ? `1px solid ${colors.primary50}`
                                              : 'none',
                                            borderBottom: isLastVisibleSection
                                              ? `1px solid ${colors.primary50}`
                                              : `1px solid ${colors.primary50}`,
                                            borderRight: 'none',
                                          }}
                                        >
                                          {t('legalEntity.licenceDetails.history.noChanges')}
                                        </CategoryHeaderNoChangesCell>
                                        <CategoryHeaderNoChangesCell
                                          style={{
                                            borderTop: isFirstSection
                                              ? `1px solid ${colors.primary50}`
                                              : 'none',
                                            borderBottom: isLastVisibleSection
                                              ? `1px solid ${colors.primary50}`
                                              : `1px solid ${colors.primary50}`,
                                            borderRight: `1px solid ${colors.primary50}`,
                                          }}
                                        >
                                          {t('legalEntity.licenceDetails.history.noChanges')}
                                        </CategoryHeaderNoChangesCell>
                                      </>
                                    )}
                                    {hasChanges && (
                                      <CategoryHeaderEmptyCell
                                        colSpan={2}
                                        style={{
                                          borderTop: isFirstSection
                                            ? `1px solid ${colors.primary50}`
                                            : 'none',
                                          borderBottom: `1px solid ${colors.primary50}`,
                                          borderRight: `1px solid ${colors.primary50}`,
                                        }}
                                      />
                                    )}
                                  </tr>
                                )}
                                {section.additionalRow &&
                                  !!showCategoryHeaderFirstCell &&
                                  hasMultiple && (
                                    <tr
                                      key={`${section.categoryName}-${section.additionalRow.name}`}
                                    >
                                      <DataRowFirstCell
                                        style={{
                                          borderBottom: `1px solid ${colors.primary50}`,
                                        }}
                                      >
                                        {additionalInfo?.name ? t(additionalInfo?.name) : ''}
                                      </DataRowFirstCell>
                                      <DataRowValueCell
                                        style={{
                                          borderBottom: `1px solid ${colors.primary50}`,
                                          borderRight: `1px solid ${colors.primary50}`,
                                        }}
                                      >
                                        {additionalInfo?.before}
                                      </DataRowValueCell>
                                      <DataRowValueCell
                                        style={{
                                          borderBottom: `1px solid ${colors.primary50}`,
                                          borderRight: `1px solid ${colors.primary50}`,
                                        }}
                                      >
                                        {additionalInfo?.after}
                                      </DataRowValueCell>
                                    </tr>
                                  )}
                                {hasChanges &&
                                  section.rows.map((row, rowIndex) => {
                                    const isLastRowInSection =
                                      rowIndex === section.rows.length - 1;
                                    const isFirstRowInSection = rowIndex === 0;
                                    const isLastRowInTable =
                                      isLastVisibleSection && isLastRowInSection;
                                    const rowKey =
                                      section.rows[0].beforeValue ||
                                      section.rows[0].afterValue;

                                    if (
                                      section.categoryName.includes(
                                        EComparisonCategory.Weapon,
                                      ) &&
                                      !isFirstRowInSection &&
                                      hasMultiple &&
                                      weaponDetailOpen !== rowKey
                                    ) {
                                      return null;
                                    }
                                    if (
                                      section.categoryName.includes(
                                        EComparisonCategory.Attachments,
                                      ) &&
                                      !isFirstRowInSection &&
                                      hasMultiple &&
                                      attachmentDetailOpen !== rowKey
                                    ) {
                                      return null;
                                    }

                                    const isWeaponSection = section.categoryName.includes(
                                      EComparisonCategory.Weapon,
                                    );
                                    const isAttachmentSection = section.categoryName.includes(
                                      EComparisonCategory.Attachments,
                                    );

                                    const handleSortButton = () => {
                                      if (isWeaponSection) {
                                        setWeaponDetailOpen(
                                          weaponDetailOpen === rowKey ? '' : rowKey,
                                        );
                                      }
                                      if (isAttachmentSection) {
                                        setAttachmentDetailOpen(
                                          attachmentDetailOpen === rowKey ? '' : rowKey,
                                        );
                                      }
                                    };

                                    return (
                                      <tr
                                        key={`${section.categoryName}-${row.translationKey}`}
                                      >
                                        <DataRowFirstCell
                                          style={{
                                            borderBottom: isLastRowInTable
                                              ? `1px solid ${colors.primary50}`
                                              : `1px solid ${colors.primary50}`,
                                            ...(isLastRowInTable && {
                                              borderBottomLeftRadius: '4px',
                                            }),
                                          }}
                                        >
                                          {isFirstRowInSection &&
                                          hasMultiple &&
                                          showSortIcon ? (
                                            <IconButton
                                              onClick={() => handleSortButton()}
                                              style={{
                                                height: '19px',
                                                width: '19px',
                                                marginBottom: '3px',
                                                marginRight: '5px',
                                              }}
                                            >
                                              <Icon
                                                type={
                                                  (isWeaponSection &&
                                                    weaponDetailOpen === rowKey) ||
                                                  (isAttachmentSection &&
                                                    attachmentDetailOpen === rowKey)
                                                    ? EIconTypes.sortDown
                                                    : EIconTypes.sortRight
                                                }
                                              />
                                            </IconButton>
                                          ) : (
                                            ''
                                          )}
                                          {`${t(row.translationKey)}${
                                            row.titleSecondPart || ''
                                          }`}
                                        </DataRowFirstCell>
                                        <DataRowValueCell
                                          style={{
                                            borderBottom: isLastRowInTable
                                              ? `1px solid ${colors.primary50}`
                                              : `1px solid ${colors.primary50}`,
                                            borderRight: `1px solid ${colors.primary50}`,
                                          }}
                                        >
                                          {formatValue(row.beforeValue, row)}
                                          {isFirstRowInSection &&
                                            !formatValue(row.beforeValue, row)?.toString()
                                              .length &&
                                            '-'}
                                        </DataRowValueCell>
                                        <DataRowValueCell
                                          style={{
                                            borderBottom: isLastRowInTable
                                              ? `1px solid ${colors.primary50}`
                                              : `1px solid ${colors.primary50}`,
                                            borderRight: `1px solid ${colors.primary50}`,
                                            ...(isLastRowInTable && {
                                              borderBottomRightRadius: '4px',
                                            }),
                                          }}
                                        >
                                          {formatValue(row.afterValue, row)}
                                          {isFirstRowInSection &&
                                            section.isNew &&
                                            ` (${t(
                                              'legalEntity.licenceDetails.linkedWeapons.new.label',
                                            )})`}
                                          {isFirstRowInSection && section.isRemoved && (
                                            <>
                                              {formatValue(row.beforeValue, row)}
                                              {` (${t(
                                                'legalEntity.licenceDetails.linkedWeapons.removed.label',
                                              )})`}
                                            </>
                                          )}
                                        </DataRowValueCell>
                                      </tr>
                                    );
                                  })}
                              </React.Fragment>
                            );
                          });
                        })()}
                      </tbody>
                    </ComparisonTable>
                  </Box>
                ) : (
                  <Grid sx={{ border: `1px solid ${colors.primary50}`, borderRadius: '4px' }}>
                    <MobileComparisonRow>
                      <MobileComparisonHeader>
                        {t('legalEntity.licenceDetails.history.before.column')}
                      </MobileComparisonHeader>
                      <MobileComparisonHeader>
                        {t('legalEntity.licenceDetails.history.after.column')}
                      </MobileComparisonHeader>
                    </MobileComparisonRow>

                    {(() => {
                      const weaponSections = comparisonSections.filter((section) =>
                        section.categoryName.includes(EComparisonCategory.Weapon),
                      );
                      const attachmentSections = comparisonSections.filter((section) =>
                        section.categoryName.includes(EComparisonCategory.Attachments),
                      );
                      const hasMultiple =
                        weaponSections.length > 1 || attachmentSections.length > 1;

                      const additionalInfo =
                        weaponSections[weaponSections.length - 1]?.additionalRow ?? null;

                      return (
                        <MobileComparisonView
                          comparisonSections={comparisonSections}
                          formatValue={formatValue}
                          t={t}
                          weaponLicenceCategory={weaponLicenceCategory || []}
                          getDictionaryValue={getDictionaryValue}
                          hasMultiple={hasMultiple}
                          additionalInfo={additionalInfo}
                          weaponDetailOpen={weaponDetailOpen}
                          attachmentDetailOpen={attachmentDetailOpen}
                          setWeaponDetailOpen={setWeaponDetailOpen}
                          setAttachmentDetailOpen={setAttachmentDetailOpen}
                        />
                      );
                    })()}
                  </Grid>
                )}
              </>
            )}
          </ModalBody>

          <ModalFooter>
            <Box sx={{ width: isDesktop ? '50%' : '100%', marginLeft: 'auto' }}>
              <Button
                id="licence_history_details_modalClose-button"
                label={t('general.close.label')}
                variant={EButtonVariants.outlined}
                size={EButtonSizes.small}
                onClick={handleClose}
                type="button"
                fullWidth
                sx={{ height: '32px' }}
              />
            </Box>
          </ModalFooter>
        </ModalContent>
      </Fade>
    </Modal>
  );
};
